import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {
  APIResponse,
  NotificationSliceInitialState,
  GetNotificationsRequestPayload,
  GetNotificationsSuccessResponse,
  MarkAllNotificationsAsReadRequestPayload,
  MarkAllNotificationsAsReadSuccessResponse,
  Notification,
} from '@/interface';

const initialState: NotificationSliceInitialState = {
  isLoading: false,
  isReadingLoading: false,
  notificationSuccess: false,
  notificationFailure: false,
  notifications: [],
  totalUnreadNotifications: 0,
  currentPage: 1,
  nextPage: null,
  totalPages: 1,
};

const notificationSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    // ================================ Get Notifications ==============================
    getNotificationsRequest(state, action: PayloadAction<GetNotificationsRequestPayload>) {
      state.isLoading = true;
      state.notificationSuccess = false;
      state.notificationFailure = false;
    },
    getNotificationsSuccess(state, action: PayloadAction<GetNotificationsSuccessResponse>) {
      state.isLoading = false;
      state.notificationSuccess = action.payload.status_code;
      state.notifications = action.payload.data.notifications;
      state.totalUnreadNotifications = action.payload.data.total_unread_notifications;
      state.currentPage = action.payload.data.current_page;
      state.nextPage = action.payload.data.next_page;
      state.totalPages = action.payload.data.total_pages;
    },
    getNotificationsFailure(state, action: PayloadAction<APIResponse>) {
      state.isLoading = false;
      state.notificationFailure = action.payload.status_code;
    },

    // ================================ Mark All Notifications as Read ==============================
    markAllAsReadRequest(state, action: PayloadAction<MarkAllNotificationsAsReadRequestPayload | undefined>) {
      state.isReadingLoading = true;
      state.notificationSuccess = false;
      state.notificationFailure = false;
    },
    markAllAsReadSuccess(state, action: PayloadAction<{response: MarkAllNotificationsAsReadSuccessResponse; id?: number}>) {
      state.isReadingLoading = false;
      state.notificationSuccess = action.payload.response.status_code;
      const notificationId = action.payload.id;

      if (notificationId !== undefined) {
        // Mark only the specific notification as read
        state.notifications = state.notifications.map(notification => (notification.id === notificationId ? {...notification, is_read: true} : notification));
        // Decrement unread count by 1
        state.totalUnreadNotifications = Math.max(0, state.totalUnreadNotifications - 1);
      } else {
        // Update all notifications to read
        state.notifications = state.notifications.map(notification => ({
          ...notification,
          is_read: true,
        }));
        state.totalUnreadNotifications = 0;
      }
    },
    markAllAsReadFailure(state, action: PayloadAction<APIResponse>) {
      state.isReadingLoading = false;
      state.notificationFailure = action.payload.status_code;
    },

    // ================================ Cancel Notification Request ==============================
    cancelNotificationRequest(state) {
      state.isLoading = false;
      state.isReadingLoading = false;
    },
    resetNotificationMessage(state) {
      state.notificationSuccess = false;
      state.notificationFailure = false;
    },

    addNotification(state, action: PayloadAction<Notification>) {
      // do not add if notification already exists
      const alreadyExists = state.notifications.some(n => n.id === action.payload.id);
      if (alreadyExists) return;

      const new_notification = action.payload;
      state.notifications = [{...new_notification}, ...state.notifications];

      state.totalUnreadNotifications += 1;
    },
  },
});

export const {
  getNotificationsRequest,
  getNotificationsSuccess,
  getNotificationsFailure,
  cancelNotificationRequest,
  resetNotificationMessage,
  markAllAsReadRequest,
  markAllAsReadSuccess,
  markAllAsReadFailure,

  addNotification,
} = notificationSlice.actions;
export default notificationSlice.reducer;
